import { useState, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../../app/useAuth";
import { useFriends } from "../../../hooks/useFriends";
import { useUserProfiles } from "../../../hooks/useUserProfiles";
import { useOutgoingAvailabilityShares } from "../../../hooks/useOutgoingAvailabilityShares";
import {
  SimpleModal,
  SimpleModalDescription,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalTitle,
} from "../../../components/ui/simple-modal";
import { FriendShareRow } from "./FriendShareRow";
import { AvailabilityShareModal } from "./AvailabilityShareModal";

/**
 * Phase 4 follow-up — modal launcher used by the My Availability page so an
 * owner can share availability without context-switching to /friends.
 *
 * Hosts a list of the user's accepted friends with one action per row:
 * "Share availability". Clicking opens the existing per-friend
 * AvailabilityShareModal (stacked) where the actual grant/save/revoke flow
 * lives. View / Remove are deliberately omitted — those belong on /friends.
 *
 * Footer carries a deep link to /friends for users who want full management.
 */
export function AvailabilityShareListModal({ open, onOpenChange }) {
  const [shareModalFriend, setShareModalFriend] = useState(null);
  const { user } = useAuth();
  const { friends } = useFriends();
  const { enrichUsers } = useUserProfiles(friends);
  const { sharesByViewerId } = useOutgoingAvailabilityShares();
  const friendProfiles = enrichUsers(friends);

  const sortedFriends = useMemo(() => {
    return [...friendProfiles].sort((a, b) => {
      const aSharing = a.uid && Boolean(sharesByViewerId[a.uid]);
      const bSharing = b.uid && Boolean(sharesByViewerId[b.uid]);
      if (aSharing !== bSharing) return aSharing ? -1 : 1;
      const aName = a.displayName || a.email || "";
      const bName = b.displayName || b.email || "";
      return aName.localeCompare(bName);
    });
  }, [friendProfiles, sharesByViewerId]);

  return (
    <>
      <SimpleModal
        open={open}
        onOpenChange={onOpenChange}
        contentClassName="sm:max-w-2xl"
      >
        <SimpleModalHeader>
          <SimpleModalTitle>Share availability with friends</SimpleModalTitle>
          <SimpleModalDescription>
            Pick a friend to grant or manage access to your busy windows
            and free-time marks. They’ll see your availability when you’re
            both invited to the same poll.
          </SimpleModalDescription>
        </SimpleModalHeader>

        <div
          className="my-4 max-h-[60vh] overflow-y-auto pr-1"
          data-testid="availability-share-list-modal-body"
        >
          {sortedFriends.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
              You don’t have any friends yet.{" "}
              <RouterLink
                to="/friends"
                className="font-semibold text-brand-primary hover:underline"
                onClick={() => onOpenChange?.(false)}
              >
                Add a friend →
              </RouterLink>
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {sortedFriends.map((friend) => {
                const isSharing = Boolean(
                  friend.uid && sharesByViewerId[friend.uid],
                );
                return (
                  <FriendShareRow
                    key={friend.email}
                    friend={friend}
                    isSharing={isSharing}
                    onShareClick={(f) => setShareModalFriend(f)}
                    testIdPrefix="modal-share-availability"
                  />
                );
              })}
            </div>
          )}
        </div>

        <SimpleModalFooter className="flex items-center justify-between gap-3 sm:flex-row sm:justify-between">
          <RouterLink
            to="/friends"
            onClick={() => onOpenChange?.(false)}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Manage all friends →
          </RouterLink>
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Done
          </button>
        </SimpleModalFooter>
      </SimpleModal>

      {shareModalFriend && (
        <AvailabilityShareModal
          ownerId={user?.uid || null}
          friend={shareModalFriend}
          existingShare={
            shareModalFriend?.uid
              ? sharesByViewerId[shareModalFriend.uid]
              : null
          }
          open={Boolean(shareModalFriend)}
          onOpenChange={(next) => {
            if (!next) setShareModalFriend(null);
          }}
        />
      )}
    </>
  );
}
